<script setup>
import { ref, computed, watch, nextTick, inject } from 'vue'
import { useMainStore } from '@/stores/main'
import { useI18n } from 'vue-i18n'
import * as Server from '@/services/Server'
import * as Alerts from '@/services/Alerts'
import CountryField from '@/components/form/CountryField.vue'
import ConfirmDialog from '@/components/ConfirmDialog.vue'

const tournament = inject('tournament')
const allTeams = inject('allTeams', ref({}))

const { t } = useI18n()
const store = useMainStore()

const emit = defineEmits([
  'update:modelValue',
  'entry-created',
  'entry-updated',
  'entry-deleted'
])

const props = defineProps(['modelValue', 'tournamentId', 'entry'])

const confirmDialog = ref(false)
const loading = ref(false)

const partecipantEmpty = {
  name: '',
  description: '',
  country: null,
  entry_number: null,
  team_id: null,
}
const newParticipant = ref({ ...partecipantEmpty })

const teamItems = computed(() =>
  Object.values(allTeams.value || {}).map(t => ({ title: t.name, value: t.id }))
)

const closeDialog = () => {
  emit('update:modelValue', false)
}

const saveNewParticipant = async () => {
  loading.value = true
  
  try {
    // Update
    if (newParticipant.value.id){
      const path = store.apiPath.update_entry.replace('{tournament}', props.tournamentId)
      const response = await Server.post(path, newParticipant.value)
      Alerts.success(t('tournaments.entryUpdatedSuccess'))
      emit('entry-updated', response.data)
      return

    // Insert
    } else {
      const path = store.apiPath.add_entry.replace('{tournament}', props.tournamentId)
      const response = await Server.post(path, newParticipant.value)
      Alerts.success(t('tournaments.entryCreatedSuccess'))
      emit('entry-created', response.data)
    }

    resetAndClose()

  } catch (error) {
    console.error('Error creating entry:', error)
    Alerts.error(t('tournaments.entryCreatedError'))
  } finally {
    loading.value = false
  }

}

const deleteEntry = async () => {
  loading.value = true
  try {
    const path = store.apiPath.delete_entry.replace('{tournament}', props.tournamentId)
    const response = await Server.post(path, newParticipant.value)
    Alerts.success(t('tournaments.entryDeletedSuccess'))
    emit('entry-deleted', newParticipant.value)
    resetAndClose()

  } catch (error) {
    console.error('Error deleting entry:', error)
    Alerts.error(error.response?.data?.error || t('tournaments.entryDeletedError'))
  } finally {
    loading.value = false
  }
}

const resetAndClose = () => {
  newParticipant.value = { ...partecipantEmpty }
  closeDialog()
}

watch(() => props.modelValue, async (newValue) => {
  // Ogni volta che si apre il dialog:
  if (newValue) {
    await nextTick()
    newParticipant.value = props.entry ? { ...props.entry } : { ...partecipantEmpty }

    const input = document.getElementById('participant-name')
    if (input) {
      input.focus()
    }
  }
})

</script>

<template>
  <v-dialog :model-value="modelValue"
    @update:model-value="$emit('update:modelValue', $event)"
    class="common-dialog"
  >
    <v-card>
      <v-card-title class="text-h5">
        {{ newParticipant.id ? $t('tournaments.editParticipant') : $t('tournaments.addNewParticipant') }}
      </v-card-title>
      <v-divider />

      <v-card-text class="pt-4">
        <v-form @submit.prevent="saveNewParticipant">
          <v-text-field
            id="participant-name"
            v-model="newParticipant.name"
            :label="$t('common.name')"
            variant="outlined"
            required
            class="mb-3"
          />

          <v-textarea
            v-model="newParticipant.description"
            :label="$t('tournaments.description')"
            variant="outlined"
            rows="3"
            class="mb-3"
          />

          <CountryField
            v-model="newParticipant.country"
            :label="$t('common.country')"
          />

          <template v-if="tournament.settings?.useTeams">
            <v-divider class="mt-4 mb-4" />
            <v-row dense>
              <v-col cols="4">
                <v-text-field
                  v-model.number="newParticipant.entry_number"
                  :label="$t('label.entry_number')"
                  variant="outlined"
                  type="number"
                  min="1"
                />
              </v-col>
              <v-col cols="8">
                <v-select
                  v-model="newParticipant.team_id"
                  :items="teamItems"
                  :label="$t('label.team')"
                  variant="outlined"
                  clearable
                />
              </v-col>
            </v-row>
          </template>

          <v-divider class="mt-2 mb-4" />

          <div class="d-flex justify-space-between">
            <div>
              <v-btn
                type="submit"
                color="primary"
                variant="elevated"
                :loading="loading"
                class="me-2"
              >
                {{ $t('common.save') }}
              </v-btn>
              <v-btn
                color="grey"
                variant="text"
                @click="closeDialog"
              >
                {{ $t('common.cancel') }}
              </v-btn>
            </div>
            <div>
              <v-btn v-if="newParticipant.id"
                color="error"
                variant="text"
                @click="confirmDialog = true"
              >
                {{ $t('common.delete') }}
              </v-btn>
            </div>
          </div>
        </v-form>
      </v-card-text>
    </v-card>

    <ConfirmDialog
      v-model="confirmDialog"
      :title="t('tournaments.deleteEntry')"
      :text="t('tournaments.deleteEntryConfirm')"
      :confirmText="t('common.confirm')"
      :cancelText="t('common.cancel')"
      confirmColor="error"
      @confirm="deleteEntry" />
      
  </v-dialog>

</template>
